import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Input } from "@/components/Input";
import { Dropdown, DropdownItem } from "@/components/Dropdown";
import { Plus, Trash, Users, Gift, ShieldAlert, Clock, BarChart, UserX, UserCheck, Trash2, Edit, X, Building2 } from "lucide-react";
import { useState } from "react";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import AppLayout from '@/layouts/AppLayout';
import { RedemptionsTab } from './Admin/RedemptionsTab';
import { ReportsTab } from './Admin/ReportsTab';
import { CelebrationsTab } from './Admin/CelebrationsTab';
import { router } from '@inertiajs/react';
import { useConfirm } from '@/hooks/useConfirm';

export default function Admin({
  currentUser = {},
  users = [],
  values = [],
  departments = [],
  stats = { totalBudget: 0, totalGiven: 0, participation: 0 },
  redemptions = [],
  redemptionStats = { pending: 0, approved: 0, fulfilled: 0, rejected: 0 },
  celebrationEvents = [],
  currentTab = 'users'
}) {

  const [activeTab, setActiveTab] = useState(currentTab);
  const { confirm, ConfirmDialog } = useConfirm();

  // Actualizar tab en la URL cuando cambie
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    router.visit(`/admin?tab=${tab}`, {
      preserveState: true,
      preserveScroll: true,
      only: [] // No recargar datos, solo cambiar URL
    });
  };

  // Local state for forms/UI only
  const [newValue, setNewValue] = useState({ hashtag: "", description: "", emoji: "🌟" });
  const [editingValue, setEditingValue] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState("active"); // all, active, inactive
  const [editingUser, setEditingUser] = useState(null); // { userId, field, value }
  const [userEditModal, setUserEditModal] = useState(null); // user object for full edit modal

  const handleAddValue = (e) => {
    e.preventDefault();
    if (editingValue) {
      router.patch(`/admin/company_values/${editingValue.id}`, {
        company_value: {
          hashtag: newValue.hashtag,
          description: newValue.description,
          emoji: newValue.emoji,
          active: true
        }
      }, {
        onSuccess: () => {
          setEditingValue(null);
          setNewValue({ hashtag: "", description: "", emoji: "🌟" });
        }
      });
    } else {
      router.post('/admin/company_values', {
        company_value: {
          hashtag: newValue.hashtag,
          description: newValue.description,
          emoji: newValue.emoji,
          active: true
        }
      }, {
        onSuccess: () => {
          setNewValue({ hashtag: "", description: "", emoji: "🌟" });
        }
      });
    }
  };

  const handleEditValue = (value) => {
    setEditingValue(value);
    setNewValue({
      hashtag: value.hashtag,
      description: value.description,
      emoji: value.emoji || "🌟"
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingValue(null);
    setNewValue({ hashtag: "", description: "", emoji: "🌟" });
  };

  const handleToggleValueStatus = (value) => {
    router.post(`/admin/company_values/${value.id}/toggle_active`, {}, {
      preserveState: true
    });
  };

  const handleUpdateUserField = (userId, field, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue < 0) return;

    router.patch(`/admin/users/${userId}/update_field`, {
      field: field,
      value: numValue
    }, {
      preserveState: false,
      preserveScroll: true,
      onSuccess: () => {
        setEditingUser(null);
      },
      onError: async (errors) => {
        console.error('Error al actualizar:', errors);
        await confirm({
          title: 'Error al actualizar',
          message: 'Ocurrió un error al actualizar el campo.',
          confirmText: 'Entendido',
          cancelText: '',
          variant: 'danger'
        });
        setEditingUser(null);
      }
    });
  };

  const handleToggleUserActive = async (user) => {
    const action = user.is_active ? 'desactivar' : 'activar';
    const confirmed = await confirm({
      title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} usuario?`,
      message: user.is_active
        ? `¿Estás seguro que deseas desactivar a ${user.full_name}? No podrá iniciar sesión ni recibir rewards.`
        : `¿Estás seguro que deseas activar a ${user.full_name}?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
      variant: user.is_active ? 'danger' : 'success'
    });

    if (confirmed) {
      router.patch(`/admin/users/${user.id}/toggle_active`, {}, {
        preserveState: false,
        preserveScroll: true
      });
    }
  };

  const handleDeleteUser = async (user) => {
    const confirmed = await confirm({
      title: '¿Eliminar usuario?',
      message: `¿Estás seguro que deseas eliminar a ${user.full_name}? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger'
    });

    if (confirmed) {
      router.delete(`/admin/users/${user.id}`, {
        preserveState: false,
        preserveScroll: true
      });
    }
  };

  const handleOpenUserEditModal = (user = null) => {
    setUserEditModal({
      ...(user || {}),
      formData: {
        full_name: user?.full_name || '',
        email: user?.email || '',
        job_title: user?.job_title || '',
        department_id: user?.department_id || '',
        cost_center: user?.cost_center || '',
        country: user?.country || 'MX',
        location: user?.location || '',
        date_of_birth: user?.date_of_birth || '',
        hired_on: user?.hired_on || ''
      }
    });
  };

  const handleSaveUserEdit = (e) => {
    e.preventDefault();
    if (!userEditModal) return;

    const isNewUser = !userEditModal.id;
    const url = isNewUser ? '/admin/users' : `/admin/users/${userEditModal.id}`;
    const method = isNewUser ? 'post' : 'patch';

    router[method](url, {
      user: userEditModal.formData
    }, {
      preserveState: false,
      onSuccess: () => {
        setUserEditModal(null);
      }
    });
  };

  // Filter Users locally for search and status
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
                         u.email?.toLowerCase().includes(userSearch.toLowerCase());

    const matchesStatus = userStatusFilter === "all" ||
                         (userStatusFilter === "active" && u.is_active !== false) ||
                         (userStatusFilter === "inactive" && u.is_active === false);

    return matchesSearch && matchesStatus;
  });

  // Contar usuarios activos e inactivos
  const activeUsersCount = users.filter(u => u.is_active !== false).length;
  const inactiveUsersCount = users.filter(u => u.is_active === false).length;

  return (
    <div className="space-y-8 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-bambu-black flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-bambu-green" />
            Panel de Administración
          </h1>
          <p className="text-gray-500 mt-1">Gestión corporativa y métricas de uso</p>
        </div>
        <div className="flex gap-2">
          {/* Future Export Buttons */}
        </div>
      </div>

      {/* --- Reports Section --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Puntos otorgados (mes)</p>
                <h3 className="text-2xl font-bold text-bambu-green">{stats.totalGiven.toLocaleString()} pts</h3>
              </div>
              <Gift className="w-8 h-8 text-bambu-green/20" />
            </div>
            <div className="mt-2 text-xs text-gray-400">
              de {stats.totalBudget.toLocaleString()} pts disponibles
            </div>
            {/* Progress Bar */}
            <div className="w-full bg-gray-100 rounded-full h-2 mt-3">
              <div className="bg-bambu-green h-2 rounded-full" style={{ width: `${Math.min((stats.totalGiven / (stats.totalBudget || 1)) * 100, 100)}%` }}></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Participación</p>
                <h3 className="text-2xl font-bold text-blue-600">{stats.participation}%</h3>
              </div>
              <Users className="w-8 h-8 text-blue-100" />
            </div>
            <div className="mt-2 text-xs text-gray-400">
              empleados enviaron reconocimiento
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Usuarios</p>
                <h3 className="text-2xl font-bold text-purple-600">{users.length}</h3>
              </div>
              <Users className="w-8 h-8 text-purple-100" />
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs">
              <span className="text-green-600 font-medium">✓ {activeUsersCount} activos</span>
              {inactiveUsersCount > 0 && (
                <span className="text-red-600 font-medium">✗ {inactiveUsersCount} inactivos</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- Tabs Navigation --- */}
      <div className="flex gap-4 border-b border-gray-200 mb-6 overflow-x-auto">
        <button
          onClick={() => handleTabChange('users')}
          className={cn("pb-2 px-4 font-medium transition-colors border-b-2 whitespace-nowrap", activeTab === 'users' ? "border-bambu-green text-bambu-green" : "border-transparent text-gray-500 hover:text-gray-700")}
        >
          Usuarios
        </button>
        <button
          onClick={() => handleTabChange('redemptions')}
          className={cn("pb-2 px-4 font-medium transition-colors border-b-2 whitespace-nowrap flex items-center gap-2", activeTab === 'redemptions' ? "border-orange-500 text-orange-600" : "border-transparent text-gray-500 hover:text-gray-700")}
        >
          <Clock className="w-4 h-4" />
          Validar Canjes
          {redemptionStats.pending > 0 && (
            <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center">
              {redemptionStats.pending}
            </Badge>
          )}
        </button>
        <button
          onClick={() => handleTabChange('celebrations')}
          className={cn("pb-2 px-4 font-medium transition-colors border-b-2 whitespace-nowrap flex items-center gap-2", activeTab === 'celebrations' ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700")}
        >
          <Gift className="w-4 h-4" />
          Celebraciones
        </button>
        <button
          onClick={() => handleTabChange('config')}
          className={cn("pb-2 px-4 font-medium transition-colors border-b-2 whitespace-nowrap", activeTab === 'config' ? "border-bambu-green text-bambu-green" : "border-transparent text-gray-500 hover:text-gray-700")}
        >
          Configuración
        </button>
        <button
          onClick={() => handleTabChange('reports')}
          className={cn("pb-2 px-4 font-medium transition-colors border-b-2 whitespace-nowrap flex items-center gap-2", activeTab === 'reports' ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700")}
        >
          <BarChart className="w-4 h-4" />
          Reportes
        </button>
      </div>

      <div className="min-h-[500px]">
        {/* --- Manage Users --- */}
        {activeTab === 'users' && (
          <Card className="border-t-4 border-t-bambu-green shadow-sm animate-in fade-in slide-in-from-bottom-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center text-xl">
                    <Users className="w-5 h-5 mr-2 text-bambu-green" />
                    Gestión de Usuarios
                  </CardTitle>
                  <p className="text-sm text-gray-500">Haz click en los valores de Bolsa, Boost, Gastado o Saldo para editarlos en vivo.</p>
                </div>
                <Button
                  onClick={() => handleOpenUserEditModal()}
                  size="sm"
                  className="bg-bambu-green hover:bg-bambu-green/90 text-white w-8 h-8 p-0 flex items-center justify-center"
                  title="Nuevo Usuario"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Input
                  placeholder="Buscar empleado por nombre..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="bg-gray-50"
                />

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 font-medium">Filtrar:</span>
                  <button
                    onClick={() => setUserStatusFilter("all")}
                    className={cn(
                      "px-3 py-1 text-xs font-medium rounded-full transition-all",
                      userStatusFilter === "all"
                        ? "bg-bambu-green text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                  >
                    Todos ({users.length})
                  </button>
                  <button
                    onClick={() => setUserStatusFilter("active")}
                    className={cn(
                      "px-3 py-1 text-xs font-medium rounded-full transition-all",
                      userStatusFilter === "active"
                        ? "bg-green-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                  >
                    Activos ({activeUsersCount})
                  </button>
                  <button
                    onClick={() => setUserStatusFilter("inactive")}
                    className={cn(
                      "px-3 py-1 text-xs font-medium rounded-full transition-all",
                      userStatusFilter === "inactive"
                        ? "bg-red-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                  >
                    Inactivos ({inactiveUsersCount})
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Usuario</th>
                        <th className="px-4 py-3">Dept / Loc</th>
                        <th className="px-4 py-3">País</th>
                        <th className="px-4 py-3 text-center">Bolsa Mes</th>
                        <th className="px-4 py-3 text-center">Boost</th>
                        <th className="px-4 py-3 text-center">Otorgado</th>
                        <th className="px-4 py-3 text-center">
                          Puntos disponibles<br />para canje
                        </th>
                        {currentUser?.role_level >= 2 && (
                          <th className="px-4 py-3 text-center" title="Rol del usuario en el sistema (Usuario/Admin) y modo de operación (normal/system)">
                            Rol / Modo
                          </th>
                        )}
                        <th className="px-4 py-3 text-right">Estatus</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {filteredUsers.length === 0 ? (
                        <tr><td colSpan={currentUser?.role_level >= 2 ? "9" : "8"} className="text-center py-8 text-gray-400">No se encontraron usuarios.</td></tr>
                      ) : (
                        filteredUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-gray-50/50 group">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <UserAvatar user={u} size={32} />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="font-bold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors"
                                      onDoubleClick={() => handleOpenUserEditModal(u)}
                                      title="Doble click para editar"
                                    >
                                      {u.full_name}
                                    </span>
                                    {u.provider === 'google_oauth2' && u.uid && (
                                      <div
                                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white border border-gray-200 shadow-sm"
                                        title="Cuenta vinculada con Google"
                                      >
                                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-xs text-gray-600 font-medium">{u.department?.name || '-'}</div>
                              <div className="text-[10px] text-gray-400">{u.location || '-'}</div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">{u.country || 'MX'}</td>
                            <td className="px-4 py-3 text-center">
                              {editingUser?.userId === u.id && editingUser?.field === 'monthly_allowance' ? (
                                <input
                                  type="number"
                                  autoFocus
                                  min="0"
                                  defaultValue={u.monthly_allowance || 0}
                                  onBlur={(e) => handleUpdateUserField(u.id, 'monthly_allowance', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateUserField(u.id, 'monthly_allowance', e.target.value);
                                    if (e.key === 'Escape') setEditingUser(null);
                                  }}
                                  className="w-20 px-2 py-1 text-xs text-center border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                <div
                                  onClick={() => setEditingUser({ userId: u.id, field: 'monthly_allowance' })}
                                  className="cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                >
                                  <span className="font-mono text-xs text-gray-700">{u.monthly_allowance || 0} pts</span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {editingUser?.userId === u.id && editingUser?.field === 'allowance_boost' ? (
                                <input
                                  type="number"
                                  autoFocus
                                  min="0"
                                  defaultValue={u.allowance_boost || 0}
                                  onBlur={(e) => handleUpdateUserField(u.id, 'allowance_boost', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateUserField(u.id, 'allowance_boost', e.target.value);
                                    if (e.key === 'Escape') setEditingUser(null);
                                  }}
                                  className="w-20 px-2 py-1 text-xs text-center border border-orange-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                              ) : (
                                <div
                                  onClick={() => setEditingUser({ userId: u.id, field: 'allowance_boost' })}
                                  className="cursor-pointer hover:bg-orange-50 px-2 py-1 rounded transition-colors"
                                >
                                  <span className="font-mono text-xs text-orange-600">+{u.allowance_boost || 0}</span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {editingUser?.userId === u.id && editingUser?.field === 'monthly_spent' ? (
                                <input
                                  type="number"
                                  autoFocus
                                  min="0"
                                  defaultValue={u.monthly_spent || 0}
                                  onBlur={(e) => handleUpdateUserField(u.id, 'monthly_spent', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateUserField(u.id, 'monthly_spent', e.target.value);
                                    if (e.key === 'Escape') setEditingUser(null);
                                  }}
                                  className="w-20 px-2 py-1 text-xs text-center border border-red-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                              ) : (
                                <div
                                  onClick={() => setEditingUser({ userId: u.id, field: 'monthly_spent' })}
                                  className="cursor-pointer hover:bg-red-50 px-2 py-1 rounded transition-colors"
                                >
                                  <span className="font-mono text-xs text-red-600">{u.monthly_spent || 0} pts</span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {editingUser?.userId === u.id && editingUser?.field === 'points_balance' ? (
                                <input
                                  type="number"
                                  autoFocus
                                  min="0"
                                  defaultValue={u.points_balance || 0}
                                  onBlur={(e) => handleUpdateUserField(u.id, 'points_balance', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateUserField(u.id, 'points_balance', e.target.value);
                                    if (e.key === 'Escape') setEditingUser(null);
                                  }}
                                  className="w-24 px-2 py-1 text-xs text-center border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                              ) : (
                                <div
                                  onClick={() => setEditingUser({ userId: u.id, field: 'points_balance' })}
                                  className="cursor-pointer hover:bg-green-50 px-2 py-1 rounded transition-colors inline-block"
                                >
                                  <span className="font-mono text-bambu-green font-bold text-xs">{u.points_balance || 0} pts</span>
                                </div>
                              )}
                            </td>
                            {currentUser?.role_level >= 2 && (
                              <td className="px-4 py-3 text-center">
                                {u.user_mode === 'system' ? (
                                  <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">
                                    Sistema
                                  </Badge>
                                ) : (
                                  <Dropdown
                                    trigger={
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          "text-[10px] transition-all",
                                          u.role_level >= 2
                                            ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 cursor-pointer hover:shadow-md"
                                            : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 cursor-pointer hover:shadow-md",
                                          u.id === currentUser?.id && "opacity-50 cursor-not-allowed hover:shadow-none"
                                        )}
                                        title={u.id === currentUser?.id ? "No puedes cambiar tu propio rol" : "Click para cambiar rol"}
                                      >
                                        {u.role_level >= 2 ? 'Admin' : 'Usuario'}
                                      </Badge>
                                    }
                                    align="center"
                                  >
                                    {u.id !== currentUser?.id && (
                                      <>
                                        <DropdownItem
                                          onClick={() => handleUpdateUserField(u.id, 'role_level', '1')}
                                          variant={u.role_level === 1 ? "default" : "ghost"}
                                        >
                                          Usuario (nivel 1)
                                        </DropdownItem>
                                        <DropdownItem
                                          onClick={() => handleUpdateUserField(u.id, 'role_level', '2')}
                                          variant={u.role_level >= 2 ? "default" : "ghost"}
                                        >
                                          Admin (nivel 2)
                                        </DropdownItem>
                                      </>
                                    )}
                                  </Dropdown>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3 text-right">
                              <Dropdown
                                trigger={
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      "text-[10px] cursor-pointer hover:shadow-md transition-all",
                                      u.is_active === false
                                        ? "bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
                                        : "bg-green-50 text-green-600 border-green-100 hover:bg-green-100"
                                    )}
                                  >
                                    {u.is_active === false ? 'Inactivo' : 'Activo'}
                                  </Badge>
                                }
                                align="right"
                              >
                                <DropdownItem
                                  onClick={() => handleOpenUserEditModal(u)}
                                  variant="default"
                                  icon={Edit}
                                >
                                  Editar
                                </DropdownItem>
                                {u.is_active ? (
                                  <DropdownItem
                                    onClick={() => handleToggleUserActive(u)}
                                    variant="danger"
                                    icon={UserX}
                                  >
                                    Desactivar
                                  </DropdownItem>
                                ) : (
                                  <DropdownItem
                                    onClick={() => handleToggleUserActive(u)}
                                    variant="success"
                                    icon={UserCheck}
                                  >
                                    Activar
                                  </DropdownItem>
                                )}
                                <DropdownItem
                                  onClick={() => handleDeleteUser(u)}
                                  variant="danger"
                                  icon={Trash2}
                                >
                                  Eliminar
                                </DropdownItem>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* --- Redemptions Management --- */}
        {activeTab === 'redemptions' && (
          <RedemptionsTab redemptions={redemptions} stats={redemptionStats} />
        )}

        {activeTab === 'celebrations' && (
          <CelebrationsTab celebration_events={celebrationEvents} />
        )}

        {/* --- Reports --- */}
        {activeTab === 'reports' && (
          <ReportsTab />
        )}

        {/* --- Manage Config --- */}
        {activeTab === 'config' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
            {/* Departments Card */}
            <Card className="border-t-4 border-t-blue-500 shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center text-xl">
                      <Building2 className="w-5 h-5 mr-2 text-blue-500" />
                      Departamentos
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">Gestiona los departamentos de la organización</p>
                  </div>
                  <Button
                    onClick={() => router.visit('/admin/departments')}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Gestionar Departamentos
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Company Values Card */}
            <Card className="border-t-4 border-t-purple-500 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <span className="text-purple-500 mr-2 text-2xl font-bold">#</span>
                  Valores Corporativos
                </CardTitle>
                <p className="text-sm text-gray-500">Hashtags disponibles para reconocimientos.</p>
              </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <form onSubmit={handleAddValue} className={cn("p-4 rounded-xl border", editingValue ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-100")}>
                  {editingValue && (
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-blue-700">Editando valor corporativo</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelEdit}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                      >
                        <X className="w-4 h-4 mr-1" /> Cancelar
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-3">
                      <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Hashtag</label>
                      <Input
                        placeholder="#Excelencia"
                        value={newValue.hashtag}
                        onChange={(e) => setNewValue({ ...newValue, hashtag: e.target.value })}
                      />
                    </div>
                    <div className="md:col-span-5">
                      <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Descripción</label>
                      <Input
                        placeholder="Breve descripción del valor..."
                        value={newValue.description}
                        onChange={(e) => setNewValue({ ...newValue, description: e.target.value })}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Emoji</label>
                      <Input
                        className="text-center"
                        maxLength={2}
                        value={newValue.emoji}
                        onChange={(e) => setNewValue({ ...newValue, emoji: e.target.value })}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Button
                        type="submit"
                        size="default"
                        disabled={!newValue.hashtag}
                        className={cn("w-full text-white h-10", editingValue ? "bg-blue-600 hover:bg-blue-700" : "bg-purple-600 hover:bg-purple-700")}
                      >
                        {editingValue ? (
                          <><Edit className="w-4 h-4 mr-1" /> Actualizar</>
                        ) : (
                          <><Plus className="w-4 h-4 mr-1" /> Agregar</>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>

                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {values.length === 0 ? (
                    <p className="text-center text-gray-400">No hay valores definidos.</p>
                  ) : (
                    values.map(val => (
                      <div key={val.id} className={cn("flex items-center justify-between p-3 rounded-lg group transition-all border", val.active ? "bg-white border-gray-200 hover:shadow-md" : "bg-gray-100 border-gray-100 opacity-60")}>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl mr-3 grayscale-[50%]">{val.emoji || '#'}</span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="hashtag" className={cn("border-purple-100", val.active ? "bg-purple-50 text-purple-700" : "bg-gray-200 text-gray-500")}>{val.hashtag}</Badge>
                              <span className="text-xs text-gray-500">{val.description}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleEditValue(val)}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn("h-7 px-2 text-xs", val.active ? "text-gray-400 hover:text-red-500 hover:bg-red-50" : "text-green-600 hover:bg-green-50")}
                            onClick={() => handleToggleValueStatus(val)}
                          >
                            {val.active ? <Trash className="w-4 h-4" /> : <span className="font-bold text-[10px]">Activar</span>}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}


      </div>

      {/* User Edit Modal */}
      {userEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {userEditModal.id ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setUserEditModal(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSaveUserEdit} className="p-6 space-y-6">
              {/* Personal Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Información Personal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre Completo <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={userEditModal.formData.full_name}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, full_name: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      value={userEditModal.formData.email}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, email: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Puesto
                    </label>
                    <Input
                      value={userEditModal.formData.job_title}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, job_title: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Departamento
                    </label>
                    <select
                      value={userEditModal.formData.department_id}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, department_id: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sin departamento</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Centro de Costos
                    </label>
                    <Input
                      value={userEditModal.formData.cost_center}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, cost_center: e.target.value }
                      })}
                      placeholder="Ej: CC-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      País
                    </label>
                    <Input
                      value={userEditModal.formData.country}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, country: e.target.value }
                      })}
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ubicación
                    </label>
                    <Input
                      value={userEditModal.formData.location}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, location: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Cumpleaños
                    </label>
                    <Input
                      type="date"
                      value={userEditModal.formData.date_of_birth}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, date_of_birth: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Ingreso
                    </label>
                    <Input
                      type="date"
                      value={userEditModal.formData.hired_on}
                      onChange={(e) => setUserEditModal({
                        ...userEditModal,
                        formData: { ...userEditModal.formData, hired_on: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end border-t pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setUserEditModal(null)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog />
    </div>
  );
}

Admin.layout = page => <AppLayout children={page} />;
